import React, { useEffect, useMemo } from "react";

import { Divider, Grid } from "@material-ui/core";
import Big from "big.js";
import { Form, Formik } from "formik";
import sum from "lodash/sum";
import { useDispatch, useSelector } from "react-redux";

import {
  DefaultVendorSplitAction,
  SaleVendorSplitAction,
} from "actions/vendorSplits";

import { SlimSecondaryButton } from "components/Button";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import { SelectField } from "components/Form/FormikControls";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SplitAllocationForm } from "components/TransactionSplits/SplitAllocationForm";

import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { SplitOrigin, SplitType, SplitTypeOptions } from "constants/splits";

import { openModalLink } from "lib/navigation";
import { getSplitKey } from "lib/splits";

import {
  getConsignmentById,
  getSaleLotById,
  getSaleLots,
  getVendorSplitByKey,
  selectConsignmentIdsByVendorIdLookup,
  selectDescriptionByVendorSplitIdLookup,
  selectSaleLotIdsByConsignmentIdLookup,
  selectVendorSplitByKeyLookup,
} from "selectors";

import { useBoolean } from "hooks";

const VendorSplitForm = ({
  deploymentId,
  primaryBusinessId,
  livestockSaleId,
  consignmentId,
  saleLotId,
  isDefault,
  onClose,
  returnTo,
}) => {
  const dispatch = useDispatch();

  const singleConsignmentVendor =
    useSelector(selectConsignmentIdsByVendorIdLookup)[primaryBusinessId]
      .length === 1;

  useEffect(() => {
    if (singleConsignmentVendor && consignmentId) {
      //   redirect to the sale default.
      openModalLink(
        ModalTypes.VendorSplit,
        {
          deploymentId,
          primaryBusinessId,
        },
        returnTo,
      );
    }
  }, [
    deploymentId,
    consignmentId,
    primaryBusinessId,
    returnTo,
    singleConsignmentVendor,
  ]);

  const action = isDefault ? DefaultVendorSplitAction : SaleVendorSplitAction;
  const handleSubmit = values => {
    const payload = {
      ...values,
      deploymentId,
    };

    // Dont include consignment if we have a saleLot.
    if (payload.saleLotId) {
      payload.consignmentId = null;
    }
    // Dont include livestock sale if we have a salelot or consignment.
    if (payload.consignmentId || payload.saleLotId) {
      payload.livestockSaleId = null;
    }

    dispatch(action.create(payload));

    onClose();
  };

  const filterFields = {
    primaryBusinessId,
    deploymentId,
    saleLotId,
    livestockSaleId,
    consignmentId,
  };

  const existingSplit = useSelector(
    getVendorSplitByKey(getSplitKey(filterFields)),
  );

  const onConfirmDelete = () => {
    dispatch(action.delete(existingSplit.id));
    onClose();
  };

  const initialValues = {
    primaryBusinessId,
    livestockSaleId,
    consignmentId,
    saleLotId,
    isDefault,
    type: SplitType.PERCENTAGE,
    splits: [
      {
        businessId: primaryBusinessId,
        amount: 1,
        shouldChargeGSTOverride: null,
      },
    ],
    ...existingSplit,
  };

  const descriptionBySplitIdLookup = useSelector(
    selectDescriptionByVendorSplitIdLookup,
  );

  const consignment = useSelector(getConsignmentById(consignmentId));
  const saleLot = useSelector(getSaleLotById(saleLotId));

  const saleLotIdsByConsignmentIdLookup = useSelector(
    selectSaleLotIdsByConsignmentIdLookup,
  );
  const saleLotLookup = useSelector(getSaleLots);

  const vendorSplitByKeyLookup = useSelector(selectVendorSplitByKeyLookup);

  const saleSplitKey = getSplitKey({
    primaryBusinessId,
    deploymentId,
    livestockSaleId,
  });
  const saleSplit = vendorSplitByKeyLookup[saleSplitKey];

  const relatedConsignmentId = consignmentId || saleLot?.consignment_id;
  const consignmentSplitKey =
    relatedConsignmentId &&
    getSplitKey({
      primaryBusinessId,
      deploymentId,
      livestockSaleId,
      consignmentId: relatedConsignmentId,
    });
  const consignmentSplit = vendorSplitByKeyLookup[consignmentSplitKey];

  const headCount = useMemo(() => {
    if (consignmentId) {
      return sum(
        saleLotIdsByConsignmentIdLookup[consignmentId]?.map(
          saleLotId => saleLotLookup[saleLotId].quantity,
        ),
      );
    }
  }, [consignmentId, saleLotIdsByConsignmentIdLookup, saleLotLookup]);

  const splitMessage = useMemo(() => {
    const action = existingSplit ? "Update" : "Create";

    if (saleLot) {
      return `${action} Vendor Split for Sale Lot #${saleLot.lot_number} (${saleLot.quantity})`;
    } else if (consignment) {
      return `${action} Vendor Split for Consignment ( ${headCount} hd in ${
        saleLotIdsByConsignmentIdLookup[consignmentId]?.length || 0
      } Sale Lots)`;
    } else {
      return `${action} Sale Vendor Split`;
    }
  }, [
    consignment,
    consignmentId,
    existingSplit,
    headCount,
    saleLot,
    saleLotIdsByConsignmentIdLookup,
  ]);

  const validate = values => {
    const errors = {};
    const { saleLotId, splits, type } = values;

    const totalAmount = splits.reduce((acc, cur) => {
      return acc.plus(new Big(cur.amount || 0));
    }, new Big(0));

    if (splits.some(split => !split.amount)) {
      errors.splits = "All splits must have an amount.";
    }

    if (splits.some(split => !split.businessId)) {
      errors.splits = "All splits must have a business.";
    }

    if (type === SplitType.PERCENTAGE) {
      if (!totalAmount.eq(1)) {
        errors.splits = `Split percentages must add up to 100% (${new Big(1)
          .minus(totalAmount)
          .times(100)}% difference).`;
      }
    } else if (!saleLotId) {
      errors.splits = "Headcount splits must be on a Sale Lot.";
    } else if (!totalAmount.eq(saleLot.quantity)) {
      errors.splits = `Total split amount must equal Sale Lot headcount (${totalAmount
        .times(-1)
        .plus(saleLot.quantity)} difference).`;
    }

    return errors;
  };
  const [isShowingConfirmDelete, showConfirmDelete, hideConfirmDelete] =
    useBoolean(false);

  const onClickEditDefault = () =>
    openModalLink(ModalTypes.VendorSplit, {
      deploymentId,
      primaryBusinessId,
    });

  const showSaleOverride = saleSplit && (consignmentId || saleLotId);
  const showConsignmentOverride = consignmentSplit && saleLotId;

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={onClose}>{splitMessage}</DialogTitle>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={validate}
      >
        <Form>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {showSaleOverride && (
                  <Grid item xs={12}>
                    This will override the existing
                    <SlimSecondaryButton onClick={onClickEditDefault}>
                      Split for the Sale
                    </SlimSecondaryButton>{" "}
                    on the vendor ({descriptionBySplitIdLookup[saleSplit.id]})
                  </Grid>
                )}
                {showConsignmentOverride && (
                  <Grid item xs={12}>
                    This will override the split defined on the consignment (
                    {descriptionBySplitIdLookup[consignmentSplit.id]})
                  </Grid>
                )}
                {(showSaleOverride || showConsignmentOverride) && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
                {saleLotId && (
                  <Grid item xs={6}>
                    <SelectField
                      name="type"
                      label="Type"
                      tooltip="Only Sale Lots can be split by head count."
                      options={SplitTypeOptions}
                      menuPortalTarget={document.body}
                    />
                  </Grid>
                )}
              </Grid>
              <SplitAllocationForm splitType={SplitOrigin.VENDOR} />
              {consignmentId && (
                <Grid item xs={12}>
                  Note: Splits on a Consignment will override those on a Sale,
                  and can be overridden further on a Sale Lot
                </Grid>
              )}
              {saleLotId && (
                <Grid item xs={12}>
                  Note: Splits on a Sale Lot will override those on a Sale or
                  Consignment.
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <SecondaryButton type="button" onClick={onClose}>
              Back
            </SecondaryButton>
            <DeleteButton
              type="button"
              onClick={showConfirmDelete}
              disabled={!existingSplit?.id}
            >
              Delete
            </DeleteButton>
            <Button data-tour="submit" type="submit">
              Save
            </Button>
          </DialogActions>
          <ConfirmDialog
            title={createModalTitle("this Vendor Split")}
            isOpen={isShowingConfirmDelete}
            onCancel={hideConfirmDelete}
            onDelete={onConfirmDelete}
          />
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};

export function VendorSplitModal(props) {
  return (
    <WaitForSync
      requiredData={[
        ApiModel.CONSIGNMENTS,
        ApiModel.BUSINESSES,
        ApiModel.SALE_VENDOR_SPLITS,
      ]}
    >
      <VendorSplitForm {...props} />
    </WaitForSync>
  );
}
